import { useState, useEffect, useRef } from 'react';
import { useXeroAuth } from '@/hooks/useXeroAuth';
import { useHighLevelAuth } from '@/hooks/useHighLevelAuth';
import { cn } from '@/utils/cn';
import { CheckCircle, XCircle, Loader2, Lock } from 'lucide-react';

type Provider = 'xero' | 'highlevel';

interface OAuthButtonProps {
  provider: Provider;
  disabled?: boolean;
  disabledMessage?: string;
}

const providerConfig = {
  xero: {
    name: 'Xero',
    bgColor: 'bg-[#13B5EA]',
    hoverBgColor: 'hover:bg-[#0E8FC4]',
    connectedText: 'Connected to Xero',
    disconnectedText: 'Not Connected',
    disconnectedSubtext: 'Connect your Xero account to start syncing',
    connectButtonText: 'Connect to Xero',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2z" />
      </svg>
    ),
  },
  highlevel: {
    name: 'CRM',
    bgColor: 'bg-[#7C3AED]',
    hoverBgColor: 'hover:bg-[#6D28D9]',
    connectedText: 'Connected to CRM',
    disconnectedText: 'Not Connected',
    disconnectedSubtext: 'Connect your CRM account to start syncing',
    connectButtonText: 'Connect to CRM',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
      </svg>
    ),
  },
};

export function OAuthButton({ provider, disabled, disabledMessage }: OAuthButtonProps) {
  const xeroAuth = useXeroAuth();
  const highlevelAuth = useHighLevelAuth();

  const auth = provider === 'xero' ? xeroAuth : highlevelAuth;
  const config = providerConfig[provider];

  const { connection, isConnected, connect, disconnect, isConnecting, isDisconnecting } = auth;

  // Track when we're waiting for the connection to be confirmed after popup closes
  const [isWaitingForConnection, setIsWaitingForConnection] = useState(false);
  const wasConnectingRef = useRef(false);

  // When isConnecting becomes true, track it
  useEffect(() => {
    if (isConnecting) {
      wasConnectingRef.current = true;
    }
  }, [isConnecting]);

  // When popup closes (isConnecting goes from true to false), start waiting
  useEffect(() => {
    if (wasConnectingRef.current && !isConnecting && !isConnected) {
      setIsWaitingForConnection(true);
      wasConnectingRef.current = false;
    }
  }, [isConnecting, isConnected]);

  // When connection is established, stop waiting
  useEffect(() => {
    if (isConnected) {
      setIsWaitingForConnection(false);
      wasConnectingRef.current = false;
    }
  }, [isConnected]);

  // Timeout the waiting state after 30 seconds to prevent infinite waiting
  useEffect(() => {
    if (isWaitingForConnection) {
      const timeout = setTimeout(() => {
        setIsWaitingForConnection(false);
      }, 30000);
      return () => clearTimeout(timeout);
    }
  }, [isWaitingForConnection]);

  const isLoading = isConnecting || isDisconnecting || isWaitingForConnection;

  if (isConnected && connection) {
    const displayName = provider === 'xero'
      ? (connection as any).tenantName
      : (connection as any).companyId;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <div>
              <p className="font-medium text-green-900">{config.connectedText}</p>
              <p className="text-sm text-green-700">
                {provider === 'xero' ? 'Organization' : 'Company'}: {displayName}
              </p>
            </div>
          </div>
          <button
            onClick={() => disconnect()}
            disabled={isLoading}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium transition-colors',
              'bg-red-600 text-white hover:bg-red-700',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isDisconnecting ? (
              <span className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </span>
            ) : (
              'Disconnect'
            )}
          </button>
        </div>
      </div>
    );
  }

  // Show disabled state with message
  if (disabled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-100 p-4 opacity-60">
          <div className="flex items-center space-x-3">
            <Lock className="h-6 w-6 text-gray-400" />
            <div>
              <p className="font-medium text-gray-700">{config.disconnectedText}</p>
              <p className="text-sm text-gray-500">
                {disabledMessage || config.disconnectedSubtext}
              </p>
            </div>
          </div>
          <button
            disabled
            className={cn(
              'rounded-md px-6 py-2.5 text-sm font-medium',
              'bg-gray-300 text-gray-500 cursor-not-allowed',
              'flex items-center space-x-2'
            )}
          >
            {config.icon}
            <span>{config.connectButtonText}</span>
          </button>
        </div>
      </div>
    );
  }

  // Determine the status display based on current state
  const getStatusDisplay = () => {
    if (isConnecting) {
      return {
        icon: <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />,
        title: 'Opening authorization...',
        subtitle: 'Please complete the sign-in in the popup window',
        borderColor: 'border-blue-200',
        bgColor: 'bg-blue-50',
      };
    }
    if (isWaitingForConnection) {
      return {
        icon: <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />,
        title: 'Confirming connection...',
        subtitle: 'Please wait while we verify your authorization',
        borderColor: 'border-blue-200',
        bgColor: 'bg-blue-50',
      };
    }
    return {
      icon: <XCircle className="h-6 w-6 text-gray-400" />,
      title: config.disconnectedText,
      subtitle: config.disconnectedSubtext,
      borderColor: 'border-gray-200',
      bgColor: 'bg-gray-50',
    };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="space-y-4">
      <div className={cn(
        'flex items-center justify-between rounded-lg border p-4',
        statusDisplay.borderColor,
        statusDisplay.bgColor
      )}>
        <div className="flex items-center space-x-3">
          {statusDisplay.icon}
          <div>
            <p className="font-medium text-gray-900">{statusDisplay.title}</p>
            <p className="text-sm text-gray-600">
              {statusDisplay.subtitle}
            </p>
          </div>
        </div>
        <button
          onClick={() => connect()}
          disabled={isLoading}
          className={cn(
            'rounded-md px-6 py-2.5 text-sm font-medium transition-colors',
            config.bgColor,
            config.hoverBgColor,
            'text-white',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'flex items-center space-x-2'
          )}
        >
          {isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Connecting...</span>
            </>
          ) : isWaitingForConnection ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Waiting for connection...</span>
            </>
          ) : (
            <>
              {config.icon}
              <span>{config.connectButtonText}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
